import { Stack, styled } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { UseQueryResult } from '@tanstack/react-query';
import Loading from 'components/Loading';
import StySidePanel from 'modules/decision/components/StySidePanel';
import {
    INDICATOR_CARD_WIDTH,
    SIDE_PANEL_WIDTH,
} from 'modules/decision/constants';
import type {
    ChartsFilters,
    ChartsQueryResponse,
    ChartsTemplatesQueryResponse,
    IndicatorsBannersProps,
} from 'modules/decision/types/indicators';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import ScrollableList from 'ui/ScrollableList/ScrollableList';
import { type ScrollOrientation } from 'ui/ScrollableList/types';
import TooltipInfo from 'ui/TooltipInfo';

import noQueryImg from './components/assets/noquery.png';
import noResultsImg from './components/assets/noresults.png';
import IndicatorCardExplore from './components/IndicatorCardExplore';
import IndicatorCardLibrary from './components/IndicatorCardLibrary';
import IndicatorCardSkeleton from './components/IndicatorCardSkeleton';
import SeeMoreModal from './components/SeeMoreModal';
import { getBannerMeta, getExploreBannerMessage } from './helpers';

const FIRST_PAGE = 1;
const SPACE_BETWEEN_INDICATORS = 16;
const SKELETON_COUNT = 3;

const HeaderContainer = styled('div')({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
});

const StyTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'underline',
}));

type SingleBannerProps = IndicatorsBannersProps & {
    useGetCharts: (
        chartsFilters: ChartsFilters,
        page: number,
        keepPrevious?: boolean,
    ) => UseQueryResult<ChartsQueryResponse | ChartsTemplatesQueryResponse>;
    displayMode?: 'onBindIndicators' | 'models' | 'catalog';
    orientation?: ScrollOrientation;
    useExploreCard?: boolean;
    topSlot?: ReactNode;
};

type BannerContentProps = {
    isExploreEmpty: boolean;
    exploreMessage: { title: string; description: string } | null;
    isQueryNotStarted: boolean;
    isLoading: boolean;
    data: ChartsQueryResponse | ChartsTemplatesQueryResponse | undefined;
    orientation: ScrollOrientation;
    useExploreCard: boolean;
};

const EMPTY_STATE_IMG_MAX_SIZE = 200;

const renderBannerContent = ({
    isExploreEmpty,
    exploreMessage,
    isQueryNotStarted,
    isLoading,
    data,
    orientation,
    useExploreCard,
}: BannerContentProps): JSX.Element => {
    if (isExploreEmpty && exploreMessage) {
        return (
            <Flex
                direction="column"
                alignItems="center"
                justifyContent="center"
                gap={2}
                sx={{ flex: 1, pb: 4 }}
            >
                <Typography variant="h4"> {exploreMessage.title}</Typography>
                <img
                    src={isQueryNotStarted ? noQueryImg : noResultsImg}
                    alt={isQueryNotStarted ? 'no query' : 'no results'}
                    style={{
                        width: '40%',
                        maxWidth: EMPTY_STATE_IMG_MAX_SIZE,
                        height: 'auto',
                    }}
                />
                <Typography
                    variant="body2SemiBold"
                    color="textSecondary"
                    sx={{ textAlign: 'center' }}
                >
                    {exploreMessage.description}
                </Typography>
            </Flex>
        );
    }
    if (isLoading || !data) {
        return (
            <ScrollableList
                orientation={orientation}
                count={SKELETON_COUNT}
                scrollStepPx={INDICATOR_CARD_WIDTH}
                gapPx={SPACE_BETWEEN_INDICATORS}
                idPrefix="indicators-banner-skeleton"
            >
                {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                    <IndicatorCardSkeleton key={i} />
                ))}
            </ScrollableList>
        );
    }
    return (
        <ScrollableList
            orientation={orientation}
            count={data.count}
            scrollStepPx={INDICATOR_CARD_WIDTH}
            gapPx={SPACE_BETWEEN_INDICATORS}
            idPrefix="indicators-banner"
        >
            {data.charts.map((indicator) =>
                useExploreCard ? (
                    <IndicatorCardExplore
                        key={indicator.key}
                        indicator={indicator}
                    />
                ) : (
                    <IndicatorCardLibrary
                        key={indicator.key}
                        indicator={indicator}
                    />
                ),
            )}
        </ScrollableList>
    );
};

const SingleIndicatorsBanner = ({
    aggregationsFilter,
    integrationsFilter,
    metricsFilter,
    dimensionsFilter,
    filtersFilter,
    search,
    vizTypesFilter,
    searchMode = 'and',
    useGetCharts,
    displayMode = 'onBindIndicators',
    orientation = 'horizontal',
    useExploreCard = false,
    topSlot = null,
}: SingleBannerProps): JSX.Element => {
    const { t } = useTranslation();
    const [showMore, setShowMore] = useState(false);

    const chartsFilters = {
        aggregations: aggregationsFilter,
        integrations: integrationsFilter,
        metrics: metricsFilter,
        dimensions: dimensionsFilter,
        filters: filtersFilter,
        title: search || undefined,
        vizTypes: vizTypesFilter,
        searchMode,
    };
    const { data, isLoading, isFetching } = useGetCharts(
        chartsFilters,
        FIRST_PAGE,
        true,
    );
    const isModel = displayMode === 'models';
    const isCatalog = displayMode === 'catalog';
    const { bannerTitle, bannerSubtitle, bannerId } = getBannerMeta(
        t,
        useExploreCard,
        isModel || isCatalog,
        data?.count,
    );

    const isQueryNotStarted = useExploreCard && metricsFilter.length === 0;
    const isNoResults = useExploreCard && !isLoading && data?.count === 0;

    const isExploreEmpty = isQueryNotStarted || isNoResults;
    const exploreMessage = getExploreBannerMessage(
        t,
        isQueryNotStarted,
        isNoResults,
    );
    const showCards = !isExploreEmpty && !isLoading && data !== undefined;

    const bannerContent = renderBannerContent({
        isExploreEmpty,
        exploreMessage,
        isQueryNotStarted,
        isLoading,
        data,
        orientation,
        useExploreCard,
    });

    return (
        <StySidePanel
            id={showCards ? bannerId : undefined}
            orientation={orientation}
            sx={
                isExploreEmpty
                    ? { maxWidth: SIDE_PANEL_WIDTH, height: '100%' }
                    : undefined
            }
        >
            {topSlot}
            <HeaderContainer>
                <Flex direction="row" alignItems="center" gap={1}>
                    <Stack>
                        <Typography variant="h5">{bannerTitle}</Typography>
                        <Typography variant="subtitle2">
                            {bannerSubtitle}
                        </Typography>
                    </Stack>
                    {useExploreCard && (
                        <TooltipInfo
                            title={t('indicators.suggestedChartsTooltip')}
                        />
                    )}
                </Flex>
                <>
                    {showCards && !isFetching && (
                        <Button
                            onClick={() => setShowMore(true)}
                            id="indicators-see-more"
                            className={
                                useExploreCard
                                    ? 'pendo-indicators-explore-see-more'
                                    : 'pendo-indicators-library-see-more'
                            }
                        >
                            <StyTypography variant="body1Bold">
                                {t('indicators.seeAllIndicators', {
                                    count: data?.count,
                                })}
                            </StyTypography>
                        </Button>
                    )}
                    {useExploreCard && isFetching && <Loading small />}
                    {showCards && showMore && (
                        <SeeMoreModal
                            chartsFilters={chartsFilters}
                            isOpen={showMore}
                            setIsOpen={setShowMore}
                            title={bannerTitle}
                            useExploreCard={useExploreCard}
                            useGetCharts={useGetCharts}
                            displaySearch={!isCatalog}
                        />
                    )}
                </>
            </HeaderContainer>
            {bannerContent}
        </StySidePanel>
    );
};

export default SingleIndicatorsBanner;
